/**
 * The packet as data: sections of units. A SECTION is one page kind for one
 * subject (the cover of Tue Oct 20, Stop 1's school copy, one recipe)
 * and starts on a fresh page. A UNIT is the smallest piece the pagination
 * moves (a grey band, a table row, the stop header, the sign-off). The layout
 * (`layout.ts`) measures every unit and packs whole units onto fixed letter
 * pages; the components render each unit by its `kind`.
 *
 * Everything here is plain data (no React), so a model can be hashed into the
 * measurement signature and built and tested without a DOM.
 */

import type { Temperature } from '@/lib/chefs/types'

/**
 * Where a unit sits in its group, for the pagination (Matt's rule: "Breaks
 * fall between sections (a whole group with its grey header moves together),
 * never mid-group"):
 * - `band`: opens a group; never ends a page, and repeats as "(continued)"
 *   when its group has to split;
 * - `row`: a line of the group;
 * - `tail`: closes a group (a total); never starts a page;
 * - `block`: stands alone and never splits.
 */
export type UnitRole = 'band' | 'row' | 'tail' | 'block'

export type StopType = 'BULK' | 'UNITIZED' | 'MIXED'

/**
 * The two copies of a stop summary (Matt, Oct 20): the SCHOOL copy stays at the school for its production records,
 * the KITCHEN copy is signed by the school and comes back with the driver. The kitchen copy prints first and carries
 * the stop's only sign-off (Matt, Oct 20: "Let's put kitchen copy on top. School copy on bottom"; "let's remove the
 * signature from the school side").
 */
export type StopCopy = 'school' | 'kitchen'

/**
 * The date stamp, the only place a page says its date (Hunter, Oct 20: "whenever you're doing a date, it should
 * always be in the date stamp. It shouldn't be different from page to page"): "TUE" over "OCT 20", top right under
 * the band, the same size on every page. A multi-day shopping list's: "TUE–FRI" over "OCT 20–23".
 */
export interface DateBox {
  weekday: string
  date: string
}

/**
 * The route's one code (Hunter, Oct 20: a phone reads several per-stop codes at once and opens the wrong stop; "do
 * a single for multi-route"). It opens the route page, every stop by name, tap one to navigate (Matt's idea), or,
 * when no page link could be signed, the whole route in Google Maps.
 */
export interface RouteCode {
  url: string
  opens: 'route-page' | 'maps'
}

/**
 * One line of a pack-list cell (Matt, Oct 20: bulk stops count containers, "17 pans" over "325 sv"): the count in
 * bold, its word small: "16" "pans", then "+ 5" "EA" for the servings left over (D54).
 */
export interface CellCount {
  count: string
  word: string
}

/** What a route instruction line leads with (Matt, Oct 20: "Instructions lead with an icon"). */
export type RouteIcon =
  | 'check'
  | 'car'
  | 'door'
  | 'pin'
  | 'contact'
  | 'phone'
  | 'flame'
  | 'calendar'
  | 'clock'
  | 'package'
  | 'info'

/** A line of a stop's INSTRUCTIONS box. `strong` lines (a check, food for a later day) print semibold. */
export interface RouteInstruction {
  icon: RouteIcon
  text: string
  strong: boolean
}

/** The stop as every stop-scoped unit names it: Matt's P2 badge, P14 name, P15 type. */
export interface StopIdentity {
  /** "1", "2"… in route order (Abner, Oct 18: "Rename stops from letters (A, B, C) to numbers (1, 2, 3)"). */
  number: string
  /** "OLHSA", the organization, printed small caps over the site. Null when it repeats the site. */
  organization: string | null
  /** "Pontiac Main Building". */
  site: string
  /** "OLHSA · Pontiac Main Building": tables that name the stop on one line. */
  label: string
  type: StopType | null
}

export interface MatrixCounts {
  /** One entry per stop column, in route order; null prints "–". */
  counts: Array<number | null>
  total: number
}

/** A production row's stop cells: the containers where its packing plans cover them, else null (the servings). */
export interface MatrixContainers {
  /** One entry per stop column, beside `counts`: "16 pans" / "+ 5 EA" over the servings; null = servings alone. */
  containers: Array<CellCount[] | null>
  /**
   * One entry per stop column: the food there rides in shared vessels (pans, boxes). A bulk cell with no containers
   * prints its servings as servings, "325 sv", never a bare count that could pass for pans (D23).
   */
  bulk: boolean[]
}

/** A container line's words: "15x Standard Hotel Pans w/ 20". */
export interface ContainerLine {
  count: string | null
  name: string
  fill: string | null
  /** An inner package riding inside the line above; printed indented under it. */
  section?: true
}

/** A line of a stop summary: what arrives, how many, in what, and its temperature boxes. */
export interface StopRowData {
  name: string
  diets: string[]
  /** "Field trip", "Holiday treat · free", "K-8 portion", the dry-run label. */
  tags: string[]
  /** One serving, as the Production Totals word it: "1/2 cup", "1 each | #8 Disher"; null when none is on file. */
  portion: string | null
  /** Servings (a unitized row ships one container per serving). */
  total: number
  /** "Servings". */
  unit: string
  /**
   * Bulk: one line per container size; unitized: "12x Containers with Clear Lid (24 oz)"; a counted item with
   * no package: "Loose". Empty only for milk with no package of its own (`milkOuterPackaging`).
   */
  containers: ContainerLine[]
  /** The kitchen copy's: how it leaves the kitchen (`deliverTemperature`), CHILLED when it is delivered cold. */
  temperature: Temperature | null
  /** The school copy's: how it is served (`serveTemperature`). */
  serveTemperature: Temperature | null
  /** Delivered cold for a hot service: the school copy says "Heat on site". */
  heatOnSite: boolean
  /** Ambient food takes no temperatures: its °F boxes print "n/a". */
  noTemps: boolean
}

export interface RecipeStopContent {
  amount: string | null
  name: string
  /** The food this recipe page makes: bold, so the cook sees their part of the container. */
  mine: boolean
}

export interface RecipeStopRow {
  /** "12×". */
  count: string
  packageType: string
  contents: RecipeStopContent[]
}

/**
 * A grey band's space above it: `group` between the production summary's
 * groups and a recipe page's blocks (Matt, Oct 20: "tighten up the spacing a
 * little bit"), `block` between the milk page's blocks (Abner, Oct 18: "More
 * spacing between sections") and above a table that follows the milk table;
 * none = the usual 10px.
 */
export type BandSpace = 'group' | 'block'

export type PacketUnitBody =
  /**
   * The grey section band (Matt's P16): "LUNCH · SERVE TODAY". `refs` print
   * "Name, p. 10" after the note. `tone: 'dark'` is the black band of a later
   * day's food on a stop summary, its note ("DO NOT SERVE TODAY") in capitals.
   */
  | {
      kind: 'band'
      text: string
      note: string | null
      level: 1 | 2
      refs?: Array<{ label: string; sectionId: string }>
      space?: BandSpace
      tone?: 'dark'
    }
  /** A table's heading inside a page: "Milk", "Production" on the production summary. */
  | { kind: 'heading'; text: string }
  // --- day cover -------------------------------------------------------------------
  /** An outlined box with a warning icon: "Ride-along: Mon 10/19 breakfast goes out today for stops 1 and 2 …". */
  | { kind: 'cover-notice'; label: string; text: string }
  /** The stops table's column labels: #, Stop, Window, Sections. */
  | { kind: 'cover-stops-head' }
  | {
      kind: 'cover-stop'
      stop: StopIdentity
      window: string | null
      windowNote: string | null
      /** "Breakfast (Mon 10/19)", "Lunch", "PM snack", "Milk", "Supplies", "Condiments". */
      sections: string[]
      warning: string | null
    }
  /** "Prep Recipes (12) ........ p.4–15": an entry of the cover's "In this packet". */
  | { kind: 'cover-toc'; label: string; sectionIds: string[] }
  // --- pull & pack ---------------------------------------------------------------
  /** The grid's key to its stop columns: "1 OPM OLHSA Pontiac Main Building · 2 HIL …". */
  | { kind: 'pull-key'; stops: Array<{ number: string; code: string; site: string }> }
  /** The Pull List's first step: pull to the totals, then pack stop by stop from the kitchen sheets (their pages). */
  | { kind: 'pull-intro'; sheetIds: string[] }
  /** A pull section's column labels: Item · Pack, Deliver temp, a column per stop on the grid, Total, ✓. */
  | { kind: 'pull-head'; stops: Array<{ number: string; code: string }> | null }
  | {
      kind: 'pull-row'
      name: string
      /** "Bulk delivery box", "In BBQ Filet combo container". */
      pack: string | null
      temperature: Temperature | null
      /** The grid's servings per stop (null prints "–"); null on the Pull List. */
      counts: Array<number | null> | null
      total: number
    }
  // --- production summary & pack list -----------------------------------------------
  /** A matrix's column head, with its Done column; `space` opens the production table that follows the milk table. */
  | { kind: 'matrix-head'; numbers: string[]; space?: BandSpace }
  | ({
      kind: 'matrix-row'
      /** The stops it goes to, "1–2", "1, 3" (the Oct 21 mockup's Stops column). */
      stops: string
      preparedYield: string[]
      expectedContainers: CellCount[] | null
      packInstructions: string[]
      name: string
      detail: string | null
      temperature: Temperature | null
      diets: string[]
      tags: string[]
    } & MatrixCounts &
      MatrixContainers)
  | ({ kind: 'matrix-subtotal'; label: string } & MatrixCounts)
  /** A section's closing line, its pages filled in at print: "Milk and other ready-made items are counted on ... (p.9)." */
  | { kind: 'section-note'; text: string; sectionIds: string[] }
  /** The pack list's last row: "Stop staged: every row for that stop is in its cart", a box per stop column. */
  | { kind: 'matrix-staged'; numbers: string[] }
  // --- shopping list -------------------------------------------------------------
  | { kind: 'shop-head'; space?: BandSpace }
  | {
      kind: 'shop-row'
      name: string
      brand?: string | null
      buy: string
      amount: string | null
      usedFor: string | null
      tag: string | null
    }
  // --- milk: what to buy (shopping list, milk page) and the milk page ---------------------
  /** The milk buying table's column head: Item, Count, Volume, Used for. */
  | { kind: 'milk-shop-head' }
  /**
   * How much of one milk to buy, two ways at one weight (Abner, Oct 19: "show
   * both options, with no prevalence to any"): its count in the package a
   * serving is ("680 cartons") and its volume ("42.5 gal"); null where the
   * data cannot say it.
   */
  | {
      kind: 'milk-shop-row'
      name: string
      purchase: string | null
      count: string | null
      volume: string | null
      usedFor: string | null
    }
  /** When the milk is served: a line per meal, "Lunch today: 359 servings at all 5 stops". */
  | {
      kind: 'milk-served'
      lines: Array<{ label: string; detail: string; note: string | null }>
    }
  /** PACK BY STOP's column head: Milk, Containers, a column per serve time, Total, Done. */
  | { kind: 'milk-pack-head'; columns: string[] }
  /** A stop's header row in PACK BY STOP. */
  | { kind: 'milk-pack-stop'; stop: StopIdentity }
  /** One milk at a stop: its containers, its servings per serve time and in all. */
  | ({ kind: 'milk-pack-row'; name: string; containers: ContainerLine[] } & MatrixCounts)
  /** PACK BY STOP's last row: each serve time's servings across the stops, and all of them. */
  | ({ kind: 'milk-pack-total' } & MatrixCounts)
  // --- recipes -------------------------------------------------------------------
  /** The recipes' section cover (Matt, Oct 20): the meals the recipes make; its title says how many recipes. */
  | {
      kind: 'recipes-cover'
      /** "Lunch" · "Serve today" · 419; "Breakfast" · "Serve Wed 10/21 · ships today, pack separately" · 352. */
      meals: Array<{ label: string; serve: string; servings: number }>
    }
  /** Matt's P11 critical-limit strip, on the milk page; a recipe page puts its limits in the METHOD (Abner, Oct 20). */
  | { kind: 'recipe-limits'; limits: string[] }
  | {
      kind: 'recipe-body'
      ingredients: Array<{ amount: string | null; name: string }>
      method: string[]
      /** Each step's own ingredients, scaled ("4.5 lb Cheddar"), by step; absent or empty when a step names none. */
      uses?: string[][]
      /** The critical limits, after the steps, each with a warning icon where a step's number would be. */
      limits: string[]
    }
  /** A long recipe's ingredients and method, a line each, so they can continue on the next page. */
  | { kind: 'recipe-ingredient'; amount: string | null; name: string }
  /** A METHOD line: a numbered step, or (`number` null) a critical limit, its warning icon where the number would be. */
  | { kind: 'recipe-step'; number: number | null; text: string; uses?: string[] }
  /** A recipe's authored tip, under the TIPS band after its HACCP points. */
  | { kind: 'recipe-tip'; text: string }
  /** A dish's Cook header (the Oct 21 canvas): "Cook · Creamy Cheddar Mac", "Yield: 30 servings | Portion: 1½ cup". */
  | { kind: 'recipe-cook-head'; dish: string; yieldLine: string | null }
  /** A HACCP point on its own row: the critical limit large on the left, what to do and the corrective action beside. */
  | { kind: 'recipe-limit'; limit: string | null; lead: string; corrective: string | null }
  /**
   * The Allergens row: "Contains Milk, Wheat." in plain text (null: none on file), then the dietary tags outlined. The
   * authored allergen HACCP text ("label every container") never prints here.
   */
  | {
      kind: 'recipe-allergens'
      contains: string[] | null
      diets: string[]
      /** Every food on the page has full allergen evidence; false: what prints may not be all of it. */
      complete: boolean
    }
  /** The Pack table's column labels, the Production Totals' columns: Pack type, Deliver temp, Portion, Total, Pack, Stops. */
  | { kind: 'recipe-pack-head' }
  | {
      kind: 'recipe-pack-row'
      /** "Unitized" (a container per serving) or "Bulk" (shared vessels). */
      packType: 'Unitized' | 'Bulk'
      /** The item under the pack type: "Mac + Broccoli", "Steamed Broccoli, à la carte · same batch". */
      name: string
      tags: string[]
      /** A later day's or a two-day meal, said under the item; null for today's. */
      meal: string | null
      /** The kitchen's temperature: how the food leaves, chilled when it is delivered cold. */
      temperature: Temperature | null
      portion: string | null
      total: number
      containers: ContainerLine[]
      /** "1–2", "3–5", "2", "1, 3". */
      stops: string
    }
  | { kind: 'recipe-bulk-head' }
  | {
      kind: 'recipe-bulk-row'
      stop: StopIdentity
      /** The row's order group where its meal splits by group ("K-8"), then its flags ("Field trip"). */
      tags: string[]
      /**
       * The meal, on a page whose food rides in more than one meal or in a later day's: "Lunch", "Breakfast · Serve
       * Wed 10/21 · ships today, pack separately" (`strong`, semibold); null on a page of one meal, today's.
       */
      meal: { text: string; strong: boolean } | null
      containers: ContainerLine[]
      /** The stop's servings of this food. */
      total: number
      /** "Servings". */
      unit: string
      temperature: Temperature | null
    }
  | { kind: 'recipe-bulk-total'; containers: string | null; total: number; unit: string }
  | { kind: 'recipe-stop'; stop: StopIdentity; meal: string; rows: RecipeStopRow[] }
  | { kind: 'recipe-total'; text: string }
  | { kind: 'recipe-haccp'; lines: string[] }
  // --- stop summaries ----------------------------------------------------------------
  /**
   * The delivery sheet's head (Hunter, Oct 20: "simplify the information on the top header", "it always looks the
   * same when you're looking at who it's going to"): DELIVER TO and the stop, no box (Abner, Oct 20), beside the date
   * stamp, then the window, the address and the contact under their own labels.
   */
  | {
      kind: 'stop-head'
      copy: StopCopy
      stop: StopIdentity
      /** "STOP 1 OF 5 · KITCHEN COPY". */
      eyebrow: string
      dateBox: DateBox
      /** "7:00 to 9:00 AM". */
      window: string | null
      /** "Lunch at 11:00 AM", "drop 1st". */
      windowNote: string | null
      address: string | null
      contactName: string | null
      contactPhone: string | null
      /** The outlined box beside the window: the kitchen copy's delivery notes, the school copy's receiving line. */
      instructionsLabel: string
      instructions: string[]
      warning: string | null
    }
  /**
   * A meal's column labels: Item, Total, Unit, Containers, Temp, then the copy's own: the school's °F, Time and OK
   * on every table alike, the kitchen's Kitchen °F, Drop-off °F, Received.
   */
  | { kind: 'stop-labels'; copy: StopCopy }
  | ({ kind: 'stop-row'; copy: StopCopy } & StopRowData)
  /** The menus' own delivery and service notes for a meal ("Delivery: …", "Service: …"). */
  | {
      kind: 'menu-notes'
      notes: Array<{ menuId: string; menuName: string; delivery: string | null; service: string | null }>
    }
  /**
   * The stop sheet's Sign-Off box (Oct 21 mockup: a sign-off on both copies; Sheila, Oct 23: titled
   * "Sign-Off"). The kitchen copy says packed by and time out; then (`dropOff`, a stop that is delivered) both
   * copies take who received it, their signature, the driver and what was missing. "Received by" and "Time" are the
   * words the sheet photo reader looks for (`sheet-vision.ts`).
   */
  | { kind: 'stop-signoff'; dropOff: boolean; copy: StopCopy }
  /** A copy's last line: the school keeps its copy for its production records; the driver brings the kitchen's back. */
  | { kind: 'copy-note'; copy: StopCopy }

export type PacketUnit = PacketUnitBody & {
  /** Unique within the packet; the measure host keys heights by it. */
  id: string
  /** Units of one group share it; null = the unit stands alone. */
  group: string | null
  role: UnitRole
  /**
   * On a repeat unit: the stream unit it copies. The copy heads a
   * continuation page only once its original has printed.
   */
  twinOf?: string
  /**
   * A heading block (a stop's header, a recipe's subtitle): a page holding
   * only such blocks never lets the group after them move to the next page;
   * the group starts here and continues instead (no header alone on a page).
   */
  keepWithNext?: boolean
}

/** A level-2 band: an order group inside a meal ("K-8", "MILK"). */
export function isSubBand(unit: PacketUnit): boolean {
  return unit.kind === 'band' && unit.level === 2
}

/** A stream unit's copy for the top of continuation pages (column labels, a heading). */
export function repeatOf(unit: PacketUnit): PacketUnit {
  return { ...unit, id: `${unit.id}-again`, twinOf: unit.id }
}

export type PacketUnitKind = PacketUnit['kind']

export type PacketSectionKind =
  | 'cover'
  | 'pull-pack'
  | 'shopping-list'
  | 'supplies'
  | 'production-summary'
  | 'milk'
  | 'recipes-cover'
  | 'recipe'
  | 'stop-summary'

/**
 * The band atop every page is the packet's tracker: the sheet's kind, big, and where it sits, "INGREDIENTS · 1 OF 2"
 * (Hunter, Oct 20: "the most important part is like, what am I looking at? ... like delivery sheet 1 of 2"; Abner:
 * "the black header is supposed to be a packet tracker"). Never a date ("remove some of the duplicate wording").
 */
export interface PacketBand {
  stopNumber?: string
  /** "RECIPE", "DELIVERY SHEET", "INGREDIENTS". */
  name: string
  /**
   * Where the sheet sits among its kind: "1 OF 23" (the day's recipes), "1 OF 5" (its stops). Null: a section that
   * runs past a page counts its pages there instead, "1 OF 2".
   */
  counter: string | null
  /**
   * The header's right side (the Oct 21 canvas): "KITCHEN COPY" on the kitchen's pages, the caterer on a kitchen stop
   * sheet, "SCHOOL COPY" on the school's; null for none. No other page names the caterer.
   */
  right: string | null
  /** `solid`: a kitchen page. `outline`: a kitchen stop sheet. */
  style: 'solid' | 'outline'
  /** The right side as a solid black tag: the school copy's "SCHOOL COPY". */
  rightTag?: boolean
}

export interface PacketTitle {
  /** "ONE DAY ONLY · ALL STOPS COMBINED". A sheet the band counts by kind adds "· 2 OF 2" past its first page. */
  eyebrow: string | null
  /**
   * The page's own name, plain: "Today's stops", "Ingredients", "Meal Orders", "Milk Orders", a recipe's name
   * (Abner, Oct 20: "Just use direct titles of what that page is"). "· Continued" is added after page one.
   */
  title: string
  /** `lg`: the cover's "Today's stops", its table's heading as well, at that heading's size. */
  size?: 'lg'
  /** A line under the title: what the page covers ("Covers this day's orders only, not the week."). */
  subtitle?: string | null
  /** A short note on the right. */
  note: string | null
  /** The date stamp on the right: every dated page carries one, the same size. */
  dateBox?: DateBox | null
  /** The route's one code, framed beside the date stamp. */
  code?: RouteCode | null
  /** A heavy rule under the title (the route's first stop opens under it). */
  rule?: boolean
}

export interface PacketSection {
  id: string
  kind: PacketSectionKind
  /** The delivery day (`YYYY-MM-DD`); null on the range shopping list of a multi-day print. */
  dayIso: string | null
  band: PacketBand
  /** Repeats on every page of the section, "· Continued" after the first; null = none (summary, stop pages). */
  title: PacketTitle | null
  /**
   * The footer's group, counted per page: "Kitchen 4 of 17".
   * `counted: false` prints the name alone ("Stop 1 · School copy"); `pages` adds the section's length: "Driver
   * route · 1 page".
   */
  footer: { key: string; label: string; counted: boolean; pages?: boolean }
  units: PacketUnit[]
  /** Units printed atop each continuation page (column labels, a heading). Never in `units`. */
  repeat: PacketUnit[]
  /** Matt: stop sheets "always stay whole on one page, so we tighten row spacing first". */
  preferOnePage: boolean
  /** A stop summary's school (`Campus.id`): its pages are preliminary only while THAT school can change its order. */
  campusId?: string
}

export interface PacketModel {
  sections: PacketSection[]
  /** Stable digest of the model's data: a re-measure key that changes whenever any printed word does. */
  signature: string
  dayCount: number
}
